import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Flame, Fuel, Globe, Layers, Loader2, Map as MapIcon, Pencil, Plus, SearchX, Trash2, TriangleAlert, Users, X,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { fmtDate, fmtEnergy, fmtInt } from '../constants';
import type { Country, GeoRegion } from '../worlds.types';
import type { GoToTab } from '../worlds.tabs';
import { withGameGroup } from '../sharedMap';
import { GeoRegionModal } from '../components/GeoRegionModal';
import { RegionCountriesModal } from '../components/RegionCountriesModal';
import { confirmAction } from '../components/ConfirmDialog';
import { FlagThumb } from '../components/FlagThumb';
import { ActionButton, ColorChip, Dash, EmptyRow, SearchField, StatusBadge } from '../components/ui';
import { fold, levelTitle, matchesSearch } from '../components/geoRegions.utils';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import rs from './GeoRegionsTab.module.scss';

// ============================================================
// Onglet Régions : géo-régions (continents, mers...) et leurs pays
// ============================================================
// Une région n'a pas de chunks à elle : elle couvre les chunks de ses pays (colonne Territoire, sur
// le niveau affiché). Un pays appartient à une région au plus, donc « ajouter » un pays d'une autre
// région le réaffecte (RegionCountriesModal). « Voir sur la carte » mène à ?tab=carte&region=<id>.
// ============================================================

/** Mini-drapeaux affichés par région (les pays au plus grand territoire), puis « +N ». */
const MINI_FLAGS = 5;
const COLS = 8;

interface RegionRow {
  r: GeoRegion;
  /** Pays de la région (liste chargée, niveau affiché). */
  count: number;
  /** Chunks peints par ces pays sur le niveau affiché. */
  chunks: number;
  top: Country[];
  /** Sans pays et sans niveau assigné : la région ne s'applique nulle part en jeu. */
  idle: boolean;
}

const plural = (n: number, one: string, many: string) => (n > 1 ? many : one);

export function GeoRegionsTab({ readOnly }: { readOnly: boolean }) {
  const regions = useWorldsStore((s) => s.geoRegions);
  const regionsVersion = useWorldsStore((s) => s.geoRegionsVersion);
  const countries = useWorldsStore((s) => s.countries);
  const levels = useWorldsStore((s) => s.levels);
  const level = useWorldsStore((s) => s.level);
  const putGeoRegion = useWorldsStore((s) => s.putGeoRegion);
  const dropGeoRegion = useWorldsStore((s) => s.dropGeoRegion);
  const [params, setParams] = useSearchParams();

  const [editing, setEditing] = useState<GeoRegion | 'new' | null>(null);
  const [managingId, setManagingId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const go: GoToTab = (tab, extra) => setParams(withGameGroup({ tab, ...extra }, params));

  const levelByName = useMemo(() => new Map(levels.map((l) => [l.name, l])), [levels]);
  const levelTitleOf = (name: string) => levelByName.get(name)?.title ?? name;

  const rows = useMemo<RegionRow[]>(() => {
    const per = new Map<string, Country[]>();
    for (const c of countries) {
      if (!c.geoRegionId) continue;
      const list = per.get(c.geoRegionId);
      if (list) list.push(c); else per.set(c.geoRegionId, [c]);
    }
    return regions.map((r) => {
      const list = per.get(r.id) ?? [];
      const top = [...list]
        .sort((a, b) => b.chunkCount - a.chunkCount || (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0))
        .slice(0, MINI_FLAGS);
      return {
        r,
        count: list.length,
        chunks: list.reduce((n, c) => n + c.chunkCount, 0),
        top,
        idle: list.length === 0 && !r.assignedLevel,
      };
    });
  }, [regions, countries]);

  const orphans = useMemo(() => countries.filter((c) => !c.geoRegionId).length, [countries]);
  const assigned = countries.length - orphans;
  const search = fold(q.trim());
  const shown = useMemo(() => rows.filter(({ r }) => matchesSearch(search, r.name, r.slug)), [rows, search]);
  const loaded = regionsVersion !== -1;
  const managing = managingId ? regions.find((r) => r.id === managingId) ?? null : null;
  const levelLabel = level ? levelTitleOf(level) : null;

  const remove = async (r: GeoRegion, n: number) => {
    const ok = await confirmAction({
      title: 'Supprimer la région',
      message: <>Supprimer la région <strong>{r.name}</strong> ?</>,
      details: (
        <>
          <div>
            {n === 0 ? 'Aucun pays n\'y est rattaché.'
              : n === 1 ? 'Son seul pays est conservé, sans région.'
                : <>Ses <strong>{fmtInt(n)}</strong> pays sont conservés, sans région.</>}
          </div>
          {r.assignedLevel && (
            <div>Le niveau <strong>{levelTitleOf(r.assignedLevel)}</strong> n'aura plus de région par défaut.</div>
          )}
        </>
      ),
    });
    if (!ok) return;
    setDeleting(r.id);
    const res = await api.deleteGeoRegion(r.id);
    setDeleting(null);
    if (!res.data) { toast.error(res.error ?? 'Suppression en échec'); return; }
    dropGeoRegion(r.id);
    if (managingId === r.id) setManagingId(null);
    const d = res.data.detachedCountries;
    toast.success(`Région ${r.name} supprimée${d ? `, ${fmtInt(d)} pays ${plural(d, 'détaché', 'détachés')}` : ''}`);
  };

  const newBtn = (label: string) => (
    <button type="button" className={styles.primaryBtn} onClick={() => setEditing('new')}>
      <Plus size={14} /> {label}
    </button>
  );

  return (
    <div className={ws.tabBody}>
      <div className={ws.toolbar}>
        <div className={rs.toolbarMain}>
          <SearchField value={q} onChange={setQ} placeholder="Rechercher par nom ou slug" className={rs.search} />
          {loaded && (
            <span className={ws.counter}>
              <strong>{fmtInt(shown.length)}</strong> {plural(shown.length, 'région', 'régions')}
              {shown.length !== regions.length && <> sur <strong>{fmtInt(regions.length)}</strong></>}
              {' · '}<strong>{fmtInt(assigned)}</strong> pays {plural(assigned, 'affecté', 'affectés')}
              {' · '}
              {orphans > 0 ? (
                <button type="button" className={ws.linkBtn} title="Voir ces pays dans l'onglet Pays"
                  onClick={() => go('pays', { filtre: 'sans-region' })}>
                  {fmtInt(orphans)} sans région
                </button>
              ) : '0 sans région'}
            </span>
          )}
        </div>
        {!readOnly && newBtn('Nouvelle région')}
      </div>

      <div className={`${ws.tableWrap} ${ws.tableScroll}`}>
        <table className={ws.table}>
          <thead>
            <tr>
              <th>Région</th>
              <th>Slug</th>
              <th className={ws.num} title="Plafond de stock de pétrole, en litres">
                <span className={ws.thIcon}><Fuel size={12} aria-hidden /> Pétrole max</span>
              </th>
              <th className={ws.num} title="Plafond de stock de gaz, en m3">
                <span className={ws.thIcon}><Flame size={12} aria-hidden /> Gaz max</span>
              </th>
              <th title="Région appliquée à tout le niveau quand aucun pays ne couvre la position">Niveau assigné</th>
              <th>Pays</th>
              <th className={ws.num} title={`Chunks peints par les pays de la région${levelLabel ? ` sur ${levelLabel}` : ''}`}>
                Territoire
              </th>
              <th className={ws.stickyEnd} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loaded && shown.map(({ r, count, chunks, top, idle }) => {
              const lvl = r.assignedLevel ? levelByName.get(r.assignedLevel) : undefined;
              const flagNames = top.map((c) => c.slug).join(', ') + (count > top.length ? ` et ${fmtInt(count - top.length)} autres` : '');
              return (
                <tr key={r.id}>
                  <td>
                    <span className={rs.regionCell}>
                      <ColorChip color={r.color} size={20} title={r.color} />
                      <span className={rs.regionText}>
                        <strong title={`Modifié le ${fmtDate(r.updatedAt)}${r.updatedBy ? ` par ${r.updatedBy}` : ''}`}>{r.name}</strong>
                        {idle && (
                          <StatusBadge tone="warn" icon={TriangleAlert}
                            title="Aucun pays et aucun niveau assigné : la région ne s'applique nulle part en jeu.">
                            sans effet en jeu
                          </StatusBadge>
                        )}
                      </span>
                    </span>
                  </td>
                  <td className={ws.nowrap}><code className={ws.codeChip}>{r.slug}</code></td>
                  <td className={`${ws.num} ${ws.nowrap}`} title={`${fmtInt(r.oilMax)} L`}>
                    {fmtEnergy(r.oilMax)}<span className={rs.unit}>L</span>
                  </td>
                  <td className={`${ws.num} ${ws.nowrap}`} title={`${fmtInt(r.gasMax)} m3`}>
                    {fmtEnergy(r.gasMax)}<span className={rs.unit}>m3</span>
                  </td>
                  <td className={ws.nowrap}>
                    {!r.assignedLevel ? <Dash /> : lvl ? (
                      <StatusBadge tone="warn" icon={Globe} title={levelTitle(lvl)}>{lvl.title}</StatusBadge>
                    ) : (
                      <StatusBadge tone="bad" icon={TriangleAlert} title="Ce niveau n'existe plus : choisis-en un autre ou retire-le.">
                        {r.assignedLevel} (inconnu)
                      </StatusBadge>
                    )}
                  </td>
                  <td>
                    <span className={rs.countCell}>
                      <button type="button" className={ws.pillBtn} title={readOnly ? 'Voir les pays' : 'Gérer les pays'}
                        onClick={() => setManagingId(r.id)}>
                        <Users size={12} aria-hidden /> <strong>{fmtInt(count)}</strong> pays
                      </button>
                      {top.length > 0 && (
                        <span className={ws.miniFlag} title={flagNames}>
                          {top.map((c) => <FlagThumb key={c.id} flag={c.flag} width={20} />)}
                          {count > top.length && <span>+{fmtInt(count - top.length)}</span>}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={ws.num} title={chunks > 0 && levelLabel ? `${fmtInt(chunks)} chunks sur ${levelLabel}` : undefined}>
                    {chunks > 0 ? <span className={ws.strongNum}>{fmtInt(chunks)}</span> : <Dash />}
                  </td>
                  <td className={ws.stickyEnd}>
                    <span className={rs.actions}>
                      <ActionButton icon={MapIcon} tone="primary" disabled={chunks === 0}
                        title={chunks > 0 ? 'Voir sur la carte' : 'Aucun chunk peint par les pays de cette région sur ce niveau'}
                        onClick={() => go('carte', { region: r.id })} />
                      {!readOnly && (
                        <>
                          <ActionButton icon={Pencil} tone="primary" title="Modifier" onClick={() => setEditing(r)} />
                          <ActionButton icon={Trash2} tone="danger" title="Supprimer" busy={deleting === r.id}
                            onClick={() => void remove(r, count)} />
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loaded && (
              <EmptyRow colSpan={COLS} icon={Loader2} spin title="Chargement des régions…" />
            )}
            {loaded && regions.length === 0 && (
              <EmptyRow colSpan={COLS} icon={Layers} title="Aucune région"
                text={readOnly
                  ? 'Aucune géo-région n\'est déclarée pour l\'instant.'
                  : 'Crée une région (ex. Europe, Amérique du Nord) puis affecte-y des pays.'}
                actions={readOnly ? undefined : newBtn('Créer une région')} />
            )}
            {loaded && regions.length > 0 && shown.length === 0 && (
              <EmptyRow colSpan={COLS} icon={SearchX} title="Aucune région ne correspond"
                text={`Aucun nom ni slug ne contient « ${q.trim()} ».`}
                actions={(
                  <button type="button" className={styles.secondaryBtn} onClick={() => setQ('')}>
                    <X size={14} /> Effacer la recherche
                  </button>
                )} />
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <GeoRegionModal region={editing === 'new' ? null : editing} levels={levels}
          onClose={() => setEditing(null)} onSaved={putGeoRegion} />
      )}

      {managing && (
        <RegionCountriesModal region={managing} readOnly={readOnly} onClose={() => setManagingId(null)} />
      )}
    </div>
  );
}
